import React, { useState } from "react";
import Draggable from "react-draggable";
import {
  Row,
  Col,
  Badge,
  Tooltip,
  message,
  Card,
  Form,
  Input,
  DatePicker,
  Select,
  Switch,
  Button,
  Row as AntRow,
  Col as AntCol,
  Popover,
  Divider,
  Space,
  Tag,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  TagOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import "./WeekView.css";
import { Event, CalendarCategory } from "./activityCalendar";
import api from "../../../services/api";

const { TextArea } = Input;
const { Option } = Select;

interface WeekViewProps {
  currentDate: Dayjs;
  events: Event[];
  categories: CalendarCategory[];
  onEventClick: (event: Event) => void;
  onDateClick: (date: Dayjs) => void;
  onEventsChange: (events: Event[]) => void;
  onEventSaved?: () => void;
  selectedCompany: { id: string; type: string } | null;
}

const WeekView: React.FC<WeekViewProps> = ({
  currentDate,
  events,
  categories,
  onEventClick,
  onDateClick,
  onEventsChange,
  onEventSaved,
}) => {
  const signature = localStorage.getItem("signature");
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [isEventDetailVisible, setIsEventDetailVisible] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [formPosition, setFormPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [detailPosition, setDetailPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [activePopover, setActivePopover] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();

  const startOfWeek = currentDate.startOf("week");
  const days: Dayjs[] = [];

  for (let i = 0; i < 7; i++) {
    days.push(startOfWeek.add(i, "days"));
  }

  const timeSlots: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    const period = hour < 12 ? "AM" : "PM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    timeSlots.push(`${displayHour}:00 ${period}`);
  }

  const getEventsForDayAndTime = (day: Dayjs, timeSlot: string) => {
    const [hourStr] = timeSlot.split(":");
    const hour = parseInt(hourStr);
    const isPM = timeSlot.includes("PM");
    let actualHour = hour;
    if (isPM && hour !== 12) actualHour += 12;
    if (!isPM && hour === 12) actualHour = 0;
    const slotTime = day.hour(actualHour).minute(0);

    return events.filter(
      (event) =>
        event.start.isSame(slotTime, "hour") && event.start.isSame(day, "day")
    );
  };

  // Get latest event for a time slot (sorted by start time)
  const getLatestEventForSlot = (day: Dayjs, timeSlot: string) => {
    const slotEvents = getEventsForDayAndTime(day, timeSlot);
    return slotEvents
      .sort((a, b) => b.start.valueOf() - a.start.valueOf())
      .slice(0, 1)[0];
  };

  // Get remaining events count for a time slot
  const getRemainingEventsCount = (day: Dayjs, timeSlot: string) => {
    const slotEvents = getEventsForDayAndTime(day, timeSlot);
    return Math.max(0, slotEvents.length - 1);
  };

  // Get all events for a time slot (for popover)
  const getAllEventsForSlot = (day: Dayjs, timeSlot: string) => {
    return getEventsForDayAndTime(day, timeSlot);
  };

  const getAllDayEvents = (day: Dayjs) => {
    return events.filter(
      (event) => event.allDay && event.start.isSame(day, "day")
    );
  };

  // Handle event click - show detail card instead of modal
  const handleEventClick = (event: Event, clickEvent: React.MouseEvent) => {
    setSelectedEvent(event);
    setActivePopover(null); // Close any open popover

    const container = document.querySelector(".week-view") as HTMLElement;
    const containerRect = container.getBoundingClientRect();

    let x = clickEvent.clientX - containerRect.left;
    let y = clickEvent.clientY - containerRect.top;

    const cardWidth = 400;
    const cardHeight = 500;

    if (x + cardWidth > containerRect.width) {
      x = containerRect.width - cardWidth - 10;
    }
    if (y + cardHeight > containerRect.height) {
      y = containerRect.height - cardHeight - 10;
    }
    if (x < 0) x = 10;
    if (y < 0) y = 10;

    setDetailPosition({ x, y });
    setIsEventDetailVisible(true);
  };

  // Handle all day switch change
  const handleAllDayChange = (allDay: boolean) => {
    const start = form.getFieldValue("start");
    const end = form.getFieldValue("end");

    if (allDay) {
      if (start) {
        form.setFieldValue("start", start.startOf("day"));
      }
      if (end) {
        form.setFieldValue("end", end.endOf("day"));
      }
    } else {
      const now = dayjs();
      if (start) {
        form.setFieldValue(
          "start",
          start.startOf("day").set("hour", 9).set("minute", 0)
        );
      }
      if (end) {
        form.setFieldValue(
          "end",
          end.startOf("day").set("hour", 17).set("minute", 0)
        );
      }
    }
  };

  const handleTimeSlotClick = (
    day: Dayjs,
    timeSlot: string,
    event: React.MouseEvent<HTMLDivElement>
  ) => {
    const [hourStr] = timeSlot.split(":");
    const hour = parseInt(hourStr);
    const isPM = timeSlot.includes("PM");
    let actualHour = hour;
    if (isPM && hour !== 12) actualHour += 12;
    if (!isPM && hour === 12) actualHour = 0;

    // Calculate start and end times
    const rect = event.currentTarget.getBoundingClientRect();
    const clickY = event.clientY - rect.top;
    const cellHeight = rect.height;
    const isAboveCenter = clickY < cellHeight / 2;

    const startHour = actualHour;
    const endHour = (actualHour + 1) % 24;
    const startMinute = isAboveCenter ? 0 : 30;
    const endMinute = isAboveCenter ? 0 : 30;

    const startTime = day.set("hour", startHour).set("minute", startMinute);
    const endTime = day.set("hour", endHour).set("minute", endMinute);

    // Get calendar container offset
    const container = document.querySelector(".week-view") as HTMLElement;
    const containerRect = container.getBoundingClientRect();

    // Calculate position relative to container
    let x = event.clientX - containerRect.left;
    let y = event.clientY - containerRect.top;

    // Ensure the card stays inside the container
    const cardWidth = 400;
    const cardHeight = 400;

    if (x + cardWidth > containerRect.width) {
      x = containerRect.width - cardWidth - 10;
    }
    if (y + cardHeight > containerRect.height) {
      y = containerRect.height - cardHeight - 10;
    }
    if (x < 0) x = 10;
    if (y < 0) y = 10;

    setFormPosition({ x, y });
    setIsFormVisible(true);
    setIsEditing(false);
    setActivePopover(null); // Close any open popover

    form.resetFields();
    form.setFieldsValue({
      title: "",
      description: "",
      start: startTime,
      end: endTime,
      allDay: false,
      category: categories[0]?.name || "Meeting",
      color: categories[0]?.color || "#1890ff",
      is_public: false,
    });
  };

  const handleSaveEvent = () => {
    form.validateFields().then(async (values) => {
      let { start, end, allDay } = values;

      // Ensure all-day events cover the full day
      if (allDay) {
        start = start.startOf("day");
        end = end.endOf("day");
      }

      const categoryId =
        categories.find((c) => c.name === values.category)?.id || "1";

      if (isEditing && selectedEvent) {
        // Update existing event
        const data = {
          signature,
          id: selectedEvent.id,
          title: values.title,
          description: values.description,
          start_date: start.format("YYYY-MM-DD HH:mm:ss"),
          end_date: end.format("YYYY-MM-DD HH:mm:ss"),
          is_all_day: allDay,
          event_type: parseInt(categoryId),
          is_public: values.is_public || false,
        };
        try {
          await api.post("activityCalendarService/updateEvent", data);
          onEventSaved?.();
          setIsFormVisible(false);
          setFormPosition(null);
          setIsEditing(false);
          setSelectedEvent(null);
          message.success("Event updated successfully");
        } catch (err) {
          message.error("Failed to update event");
        }
      } else {
        // Create new event
        const data = {
          signature,
          title: values.title,
          description: values.description,
          start_date: start.format("YYYY-MM-DD HH:mm:ss"),
          end_date: end.format("YYYY-MM-DD HH:mm:ss"),
          is_all_day: allDay,
          event_type: parseInt(categoryId),
          is_public: values.is_public || false,
        };
        try {
          await api.post("activityCalendarService/createEvent", data);
          onEventSaved?.();
          setIsFormVisible(false);
          setFormPosition(null);
          message.success("Event saved successfully");
        } catch (err) {
          message.error("Failed to create event");
        }
      }
    });
  };

  const handleCancelEvent = () => {
    setIsFormVisible(false);
    setFormPosition(null);
    setIsEditing(false);
    setSelectedEvent(null);
  };

  // Handle edit event - open the form card with event data
  const handleEditEvent = () => {
    if (selectedEvent) {
      // Close detail card and open form card in the same position
      setIsEventDetailVisible(false);
      setIsFormVisible(true);
      setIsEditing(true);

      // Use the same position as the detail card
      if (detailPosition) {
        setFormPosition(detailPosition);
      }

      // Pre-fill the form with event data
      form.setFieldsValue({
        title: selectedEvent.title,
        description: selectedEvent.description,
        start: selectedEvent.start,
        end: selectedEvent.end,
        allDay: selectedEvent.allDay,
        category: selectedEvent.category,
        color: selectedEvent.color,
        is_public: selectedEvent.is_public || false,
      });
    }
  };

  // Handle delete event
  const handleDeleteEvent = async () => {
    if (!selectedEvent?.id) return;

    try {
      await api.post("activityCalendarService/deleteEvent", {
        signature,
        id: selectedEvent.id,
      });
      message.success("Event deleted successfully");
      setIsEventDetailVisible(false);
      setSelectedEvent(null);
      onEventSaved?.();
    } catch (err) {
      message.error("Failed to delete event");
    }
  };

  const handleCloseDetail = () => {
    setIsEventDetailVisible(false);
    setSelectedEvent(null);
  };

  // Handle popover visibility change
  const handlePopoverVisibleChange = (visible: boolean, popoverId: string) => {
    if (visible) {
      setActivePopover(popoverId);
    } else {
      setActivePopover(null);
    }
  };

  // Popover content for remaining events in a time slot
  const renderRemainingEventsPopover = (day: Dayjs, timeSlot: string) => {
    const slotEvents = getAllEventsForSlot(day, timeSlot);

    return (
      <div className="remaining-events-popover">
        <div className="popover-title">
          All Events at {timeSlot} on {day.format("MMM D")}
        </div>
        <div className="popover-events-list">
          {slotEvents.map((event) => (
            <div
              key={event.id}
              className="popover-event-item"
              onClick={(e) => {
                e.stopPropagation();
                handleEventClick(event, e as any);
              }}
            >
              <div
                className="popover-event-color"
                style={{ backgroundColor: event.color }}
              />
              <div className="popover-event-content">
                <div className="popover-event-title">{event.title}</div>
                <div className="popover-event-time">
                  {event.start.format("h:mm A")} - {event.end.format("h:mm A")}
                </div>
                {event.description && (
                  <div className="popover-event-description">
                    {event.description}
                  </div>
                )}
                {event.is_public && (
                  <div className="popover-event-public">🌐 Public Event</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const formatEventTime = (event: Event): string => {
    if (event.allDay) return "All day";
    return `${event.start.format("h:mm A")} - ${event.end.format("h:mm A")}`;
  };

  const isAllDay = Form.useWatch("allDay", form);

  return (
    <div className="week-view" style={{ position: "relative" }}>
      <Row className="week-header">
        <Col span={3} className="time-column">
          <div className="time-header">Time</div>
        </Col>
        {days.map((day) => (
          <Col
            key={day.format("YYYY-MM-DD")}
            span={3}
            className="day-header"
            onClick={() => onDateClick(day)}
            style={{ cursor: "pointer" }}
          >
            <div className="day-name">{day.format("ddd")}</div>
            <div
              className={`day-number ${
                day.isSame(dayjs(), "day") ? "today" : ""
              }`}
            >
              {day.format("D")}
            </div>
          </Col>
        ))}
      </Row>

      {/* All-day events row */}
      <Row className="all-day-row">
        <Col span={3} className="all-day-label">
          All Day
        </Col>
        {days.map((day) => {
          const allDayEvents = getAllDayEvents(day);

          // Get latest all-day event (sorted by start time)
          const getLatestAllDayEvent = () => {
            return allDayEvents
              .sort((a, b) => b.start.valueOf() - a.start.valueOf())
              .slice(0, 1)[0];
          };

          // Get remaining all-day events count
          const getRemainingAllDayEventsCount = () => {
            return Math.max(0, allDayEvents.length - 1);
          };

          const latestEvent = getLatestAllDayEvent();
          const remainingCount = getRemainingAllDayEventsCount();
          const popoverId = `all-day-${day.format("YYYY-MM-DD")}`;

          // Popover content for remaining all-day events
          const renderAllDayRemainingEventsPopover = () => {
            return (
              <div className="remaining-events-popover">
                <div className="popover-title">
                  All Day Events on {day.format("MMM D")}
                </div>
                <div className="popover-events-list">
                  {allDayEvents.map((event) => (
                    <div
                      key={event.id}
                      className="popover-event-item"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(event, e as any);
                      }}
                    >
                      <div
                        className="popover-event-color"
                        style={{ backgroundColor: event.color }}
                      />
                      <div className="popover-event-content">
                        <div className="popover-event-title">{event.title}</div>
                        {event.description && (
                          <div className="popover-event-description">
                            {event.description}
                          </div>
                        )}
                        {event.is_public && (
                          <div className="popover-event-public">
                            🌐 Public Event
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          };

          return (
            <Col
              key={day.format("YYYY-MM-DD")}
              span={3}
              className="all-day-cell"
            >
              {/* Show latest all-day event */}
              {latestEvent && (
                <Tooltip key={latestEvent.id} title={latestEvent.title}>
                  <div
                    className="all-day-event"
                    style={{ borderLeft: `3px solid ${latestEvent.color}` }}
                    onClick={(e) => handleEventClick(latestEvent, e)}
                  >
                    {latestEvent.title}
                  </div>
                </Tooltip>
              )}

              {/* Show remaining all-day events count with popover */}
              {remainingCount > 0 && (
                <Popover
                  content={renderAllDayRemainingEventsPopover()}
                  trigger="click"
                  placement="bottom"
                  overlayClassName="remaining-events-popover-overlay"
                  open={activePopover === popoverId}
                  onOpenChange={(visible) =>
                    handlePopoverVisibleChange(visible, popoverId)
                  }
                >
                  <div
                    className="more-events-indicator"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="more-events-badge">
                      +{remainingCount} more
                    </div>
                  </div>
                </Popover>
              )}
            </Col>
          );
        })}
      </Row>

      {/* Time slots */}
      {timeSlots.map((timeSlot) => (
        <Row key={timeSlot} className="time-slot">
          <Col span={3} className="time-label">
            {timeSlot}
          </Col>
          {days.map((day) => {
            const latestEvent = getLatestEventForSlot(day, timeSlot);
            const remainingCount = getRemainingEventsCount(day, timeSlot);
            const popoverId = `time-${day.format("YYYY-MM-DD")}-${timeSlot}`;

            return (
              <Col
                key={day.format("YYYY-MM-DD")}
                span={3}
                className="time-slot-cell"
                onClick={(e) => handleTimeSlotClick(day, timeSlot, e)}
              >
                {/* Show latest event */}
                {latestEvent && (
                  <Tooltip
                    key={latestEvent.id}
                    title={`${latestEvent.title} (${latestEvent.start.format(
                      "h:mm A"
                    )})`}
                  >
                    <div
                      className="time-event"
                      style={{ borderLeft: `3px solid ${latestEvent.color}` }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEventClick(latestEvent, e);
                      }}
                    >
                      {latestEvent.title}
                    </div>
                  </Tooltip>
                )}

                {/* Show remaining events count with popover */}
                {remainingCount > 0 && (
                  <Popover
                    content={renderRemainingEventsPopover(day, timeSlot)}
                    trigger="click"
                    placement="bottom"
                    overlayClassName="remaining-events-popover-overlay"
                    open={activePopover === popoverId}
                    onOpenChange={(visible) =>
                      handlePopoverVisibleChange(visible, popoverId)
                    }
                  >
                    <div
                      className="more-events-indicator"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="more-events-badge">
                        +{remainingCount} more
                      </div>
                    </div>
                  </Popover>
                )}
              </Col>
            );
          })}
        </Row>
      ))}

      {/* Event Form Card */}
      {isFormVisible && formPosition && (
        <Draggable
          handle=".card-header"
          bounds=".week-view"
          defaultPosition={formPosition}
        >
          <Card
            className="event-form-card"
            style={{
              width: 450,
              zIndex: 1000,
              position: "absolute",
              top: 0,
              left: 0,
            }}
            styles={{
              body: { padding: 0 },
            }}
          >
            {/* Custom Header */}
            <div className="card-header">
              <div className="card-header-content">
                <div className="card-title-section">
                  <div className="card-icon">📅</div>
                  <div>
                    <div className="card-title">
                      {isEditing ? "Edit Event" : "Create New Event"}
                    </div>
                    <div className="card-subtitle">
                      {isEditing ? "Update event details" : "Add event details"}
                    </div>
                  </div>
                </div>
                <Button
                  type="text"
                  onClick={handleCancelEvent}
                  className="close-btn"
                  icon={<span>×</span>}
                />
              </div>
            </div>

            {/* Form Content */}
            <div className="card-body">
              <Form form={form} layout="vertical" className="event-form">
                {/* Title Section */}
                <div className="form-section">
                  <div className="section-label">
                    <span className="section-icon">✏️</span>
                    Event Title
                  </div>
                  <Form.Item
                    name="title"
                    rules={[
                      { required: true, message: "Please enter event title" },
                    ]}
                  >
                    <Input
                      placeholder="What's happening?"
                      className="modern-input"
                      size="large"
                    />
                  </Form.Item>
                </div>

                {/* Description Section */}
                <div className="form-section">
                  <div className="section-label">
                    <span className="section-icon">📝</span>
                    Description
                  </div>
                  <Form.Item name="description">
                    <TextArea
                      rows={2}
                      placeholder="Add some details about your event..."
                      className="modern-textarea"
                    />
                  </Form.Item>
                </div>

                {/* Settings Row */}
                <div className="settings-row">
                  <div className="form-section compact">
                    <div className="section-label">All Day</div>
                    <Form.Item name="allDay" valuePropName="checked">
                      <Switch
                        onChange={handleAllDayChange}
                        className="modern-switch"
                        checkedChildren="ON"
                        unCheckedChildren="OFF"
                      />
                    </Form.Item>
                  </div>

                  <div className="form-section compact">
                    <div className="section-label">Category</div>
                    <Form.Item name="category">
                      <Select
                        className="modern-select"
                        placeholder="Select category"
                      >
                        {categories
                          .filter(
                            (cat) =>
                              cat.name !== "Birthday" &&
                              cat.name !== "Anniversary" &&
                              cat.name !== "Holiday"
                          )
                          .map((cat) => (
                            <Option key={cat.id} value={cat.name}>
                              <div className="category-option">
                                <div
                                  className="category-color"
                                  style={{ backgroundColor: cat.color }}
                                />
                                <span>{cat.name}</span>
                              </div>
                            </Option>
                          ))}
                      </Select>
                    </Form.Item>
                  </div>
                  <div className="form-section compact">
                    <div className="section-label">Public</div>
                    <Form.Item name="is_public" valuePropName="checked">
                      <Switch
                        className="modern-switch"
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* Date & Time Section */}
                <div className="form-section">
                  <div className="section-label">
                    <span className="section-icon">⏰</span>
                    Date & Time
                  </div>
                  <div className="datetime-grid">
                    <Form.Item
                      name="start"
                      label="Start"
                      rules={[
                        { required: true, message: "Please select start time" },
                      ]}
                      className="datetime-item"
                    >
                      <DatePicker
                        showTime={
                          !isAllDay
                            ? {
                                format: "h:mm A",
                                use12Hours: true,
                                hourStep: 1,
                                minuteStep: 15,
                                showNow: false,
                              }
                            : false
                        }
                        format={isAllDay ? "MMM D, YYYY" : "MMM D, YYYY h:mm A"}
                        className="modern-datepicker"
                        disabled={isAllDay}
                        disabledTime={isAllDay ? () => ({}) : undefined}
                        placeholder="Start"
                        suffixIcon={<span>📅</span>}
                      />
                    </Form.Item>

                    <Form.Item
                      name="end"
                      label="End"
                      rules={[
                        { required: true, message: "Please select end time" },
                      ]}
                      className="datetime-item"
                    >
                      <DatePicker
                        showTime={
                          !isAllDay
                            ? {
                                format: "h:mm A",
                                use12Hours: true,
                                hourStep: 1,
                                minuteStep: 15,
                                showNow: false,
                              }
                            : false
                        }
                        format={isAllDay ? "MMM D, YYYY" : "MMM D, YYYY h:mm A"}
                        className="modern-datepicker"
                        disabled={isAllDay}
                        disabledTime={isAllDay ? () => ({}) : undefined}
                        placeholder="End"
                        suffixIcon={<span>📅</span>}
                      />
                    </Form.Item>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="action-buttons">
                  <Button
                    onClick={handleCancelEvent}
                    className="cancel-btn"
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSaveEvent}
                    className="save-btn"
                    size="large"
                    icon={<span>💫</span>}
                  >
                    {isEditing ? "Update Event" : "Create Event"}
                  </Button>
                </div>
              </Form>
            </div>
          </Card>
        </Draggable>
      )}

      {/* Event Detail Card */}
      {isEventDetailVisible && selectedEvent && detailPosition && (
        <Draggable
          handle=".card-header"
          bounds=".week-view"
          defaultPosition={detailPosition}
        >
          <Card
            className="event-detail-card"
            style={{
              width: 400,
              zIndex: 1001,
              position: "absolute",
              top: 0,
              left: 0,
            }}
            styles={{
              body: { padding: 0 },
            }}
          >
            {/* Custom Header */}
            <div
              className="card-header detail-header"
              style={{
                background: `linear-gradient(135deg, ${selectedEvent.color}20 0%, ${selectedEvent.color}40 100%)`,
                borderLeft: `4px solid ${selectedEvent.color}`,
              }}
            >
              <div className="card-header-content">
                <div className="card-title-section">
                  <div
                    className="card-icon"
                    style={{ backgroundColor: selectedEvent.color }}
                  >
                    <CalendarOutlined
                      style={{ color: "white", fontSize: "16px" }}
                    />
                  </div>
                  <div>
                    <div className="card-title">{selectedEvent.title}</div>
                    <div className="card-subtitle">
                      {formatEventTime(selectedEvent)}
                    </div>
                  </div>
                </div>
                <Button
                  type="text"
                  onClick={handleCloseDetail}
                  className="close-btn"
                  icon={<CloseOutlined />}
                />
              </div>
            </div>

            {/* Detail Content */}
            <div className="card-body">
              <div className="event-details">
                {/* Event Information */}
                <div className="detail-section">
                  <div className="detail-item">
                    <div className="detail-label">
                      <ClockCircleOutlined className="detail-icon" />
                      Time
                    </div>
                    <div className="detail-value">
                      {selectedEvent.allDay ? (
                        "All Day"
                      ) : (
                        <>
                          {selectedEvent.start.format(
                            "ddd, MMM D, YYYY h:mm A"
                          )}
                          <br />
                          to{" "}
                          {selectedEvent.end.format("ddd, MMM D, YYYY h:mm A")}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="detail-item">
                    <div className="detail-label">
                      <TagOutlined className="detail-icon" />
                      Category
                    </div>
                    <div className="detail-value">
                      <Tag
                        color={selectedEvent.color}
                        style={{ border: `1px solid ${selectedEvent.color}` }}
                      >
                        {selectedEvent.category}
                      </Tag>
                    </div>
                  </div>

                  {selectedEvent.location && (
                    <div className="detail-item">
                      <div className="detail-label">
                        <EnvironmentOutlined className="detail-icon" />
                        Location
                      </div>
                      <div className="detail-value">
                        {selectedEvent.location}
                      </div>
                    </div>
                  )}

                  <div className="detail-item">
                    <div className="detail-label">
                      <FileTextOutlined className="detail-icon" />
                      Visibility
                    </div>
                    <div className="detail-value">
                      <Tag color={selectedEvent.is_public ? "blue" : "default"}>
                        {selectedEvent.is_public
                          ? "Public Event"
                          : "Private Event"}
                      </Tag>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {selectedEvent.description && (
                  <>
                    <Divider style={{ margin: "16px 0" }} />
                    <div className="detail-section">
                      <div className="detail-item">
                        <div className="detail-label">
                          <FileTextOutlined className="detail-icon" />
                          Description
                        </div>
                        <div className="detail-value description-text">
                          {selectedEvent.description}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Action Buttons */}
                <Divider style={{ margin: "16px 0" }} />
                <div className="detail-actions">
                  <Space
                    style={{ width: "100%", justifyContent: "space-between" }}
                  >
                    <Button
                      type="primary"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={handleDeleteEvent}
                      size="middle"
                      disabled={selectedEvent?.is_generated}
                    >
                      Delete
                    </Button>
                    <Button
                      type="primary"
                      icon={<EditOutlined />}
                      onClick={handleEditEvent}
                      size="middle"
                      disabled={selectedEvent?.is_generated}
                    >
                      Edit Event
                    </Button>
                  </Space>
                </div>
              </div>
            </div>
          </Card>
        </Draggable>
      )}
    </div>
  );
};

export default WeekView;
